import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { appBannerAPI, locationAPI } from '../../../services/api';
import { showSuccessToast, showErrorToast } from '../../../utils/notificationUtils';
import { resolveImageUrl } from '../../../utils/imageUtils';
import { X, UploadCloud, Globe, Type, ToggleRight, ToggleLeft, ArrowLeft } from 'lucide-react';
import './AppBannerForm.css';

// Styles for react-select to match premium dashboard design
const getSelectStyles = (isRTL) => ({
  control: (base, state) => ({
    ...base,
    padding: '4px',
    borderRadius: '8px',
    borderColor: state.isFocused ? '#8548A8' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(133, 72, 168, 0.1)' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#8548A8' : '#9ca3af'
    },
    fontSize: '1rem',
    fontFamily: '"Red Hat Display", sans-serif',
    direction: isRTL ? 'rtl' : 'ltr',
    minHeight: '48px'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#8548A8' : state.isFocused ? '#f3e8ff' : 'white',
    color: state.isSelected ? 'white' : '#374151',
    '&:active': {
      backgroundColor: '#8548A8'
    },
    cursor: 'pointer',
    direction: isRTL ? 'rtl' : 'ltr',
    textAlign: isRTL ? 'right' : 'left'
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    zIndex: 100
  }),
  placeholder: (base) => ({
    ...base,
    color: '#9ca3af'
  }),
  singleValue: (base) => ({
    ...base,
    color: '#1f2937'
  })
});

const AppBannerForm = ({ banner, onSuccess, onCancel }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    country_code: '',
    is_active: true,
    image: null,
  });
  const [preview, setPreview] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [foundBanner, setFoundBanner] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCountries();
    if (banner) {
      setFormData({
        title: banner.title || '',
        country_code: banner.country_code || '',
        is_active: banner.is_active ?? true,
        image: null,
      });
      setPreview(resolveImageUrl(banner.image_url));
    } else {
      setFormData({
        title: '',
        country_code: '',
        is_active: true,
        image: null,
      });
      setPreview(null);
    }
  }, [banner]);

  useEffect(() => {
    const checkExistingBanner = async () => {
      // Check for existing banner regardless of add/edit mode, if a country is selected
      if (!formData.country_code) {
        return;
      }

      try {
        const response = await appBannerAPI.getBanners({ country_code: formData.country_code });
        const bannersList = response.data?.data || [];
        
        // Find if another banner exists for this country (ignoring the current one if editing)
        const existing = bannersList.find(b => 
          b.country_code === formData.country_code && 
          (!banner || b.id !== banner.id)
        );

        if (existing) {
          setFoundBanner(existing);
          setShowConfirmModal(true);
        }
      } catch (error) {
        console.error('Error checking existing banner:', error);
      }
    };

    const timer = setTimeout(checkExistingBanner, 300);
    return () => clearTimeout(timer);
  }, [formData.country_code, banner, navigate, t]);

  const fetchCountries = async () => {
    try {
      const response = await locationAPI.getCountries();
      const responseData = response.data?.data || response.data;
      if (Array.isArray(responseData)) {
        setCountries(responseData);
      } else if (responseData?.countries && Array.isArray(responseData.countries)) {
        setCountries(responseData.countries);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setFormData(prev => ({ ...prev, image: null }));
    if (banner) {
      setPreview(resolveImageUrl(banner.image_url));
    } else {
      setPreview(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.country_code || (!banner && !formData.image)) {
      showErrorToast(t('error.fillRequired'));
      return;
    }

    setLoading(true);
    try {
      if (banner) {
        await appBannerAPI.updateBanner(banner.id, formData);
        showSuccessToast(t('success.updated'));
      } else {
        await appBannerAPI.createBanner(formData);
        showSuccessToast(t('success.created'));
      }
      onSuccess();
    } catch (error) {
      showErrorToast(error.response?.data?.message || t('error.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSwitch = () => {
    if (foundBanner) {
      navigate(`/admin/dashboard/cms/banners/edit/${foundBanner.id}`);
    }
    setShowConfirmModal(false);
  };

  const handleCancelSwitch = () => {
    setFormData(prev => ({ ...prev, country_code: '' }));
    setFoundBanner(null);
    setShowConfirmModal(false);
  };

  const handleSelectChange = (option) => {
    setFormData(prev => ({
      ...prev,
      country_code: option ? option.value : ''
    }));
  };

  const countryOptions = [
    { value: 'all', label: t('adminDashboard.menu.all') },
    ...countries.map(c => ({
      value: c.code,
      label: i18n.language === 'ar' 
        ? `${c.name_ar || c.name} (${c.code})` 
        : `${c.name_en || c.name} (${c.code})`
    }))
  ];

  const selectedCountryOption = countryOptions.find(opt => opt.value === formData.country_code) || null;
  const isRTL = i18n.dir() === 'rtl';

  return (
    <div className="ab-form-container">
      <div className="ab-form-header">
        <button className="ab-back-btn" onClick={onCancel}>
          <ArrowLeft size={20} />
          <span>{t('adminDashboard.cms.back_to_list')}</span>
        </button>
        <h3>{banner ? t('adminDashboard.cms.edit_banner') : t('adminDashboard.cms.add_banner')}</h3>
      </div>

      <div className="ab-form-card">
        <form onSubmit={handleSubmit} className="ab-main-form">
          <div className="ab-form-row">
            <div className="ab-form-group">
              <label><Type size={16} /> {t('adminDashboard.cms.banner_title')}</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder={t('adminDashboard.cms.banner_title')}
                required
              />
            </div>

            <div className="ab-form-group">
              <label><Globe size={16} /> {t('adminDashboard.cms.country')}</label>
              <Select
                options={countryOptions}
                value={selectedCountryOption}
                onChange={handleSelectChange}
                placeholder={t('adminDashboard.cms.select_country')}
                styles={getSelectStyles(isRTL)}
                isRTL={isRTL}
                className="ab-searchable-select"
                classNamePrefix="select"
                required
              />
              <input 
                type="hidden" 
                name="country_code" 
                value={formData.country_code} 
                required 
              />
            </div>
          </div>

          <div className="ab-form-row">
            <div className="ab-form-group">
              <div className="ab-toggle-column">
                <label>{t('adminDashboard.cms.status')}</label>
                <div 
                  className={`ab-toggle-box ${formData.is_active ? 'active' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
                >
                  {formData.is_active ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                  <span>{formData.is_active ? t('common.active') : t('common.inactive')}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="ab-form-group full-width">
            <label><UploadCloud size={16} /> {t('adminDashboard.cms.banner_image')}</label>
            <div 
              className="ab-image-upload-wrapper"
              onClick={() => {
                if (!preview || (formData.image || (banner && preview !== resolveImageUrl(banner.image_url)))) {
                  fileInputRef.current.click();
                }
              }}
            >
              {preview ? (
                <div className="ab-image-preview-large">
                  <img src={preview} alt="Preview" />
                  <div className="ab-image-overlay-large">
                    <div className="ab-image-actions-centered">
                      <button 
                        type="button" 
                        className="ab-image-btn replace"
                        onClick={() => fileInputRef.current.click()}
                      >
                        <UploadCloud size={18} /> {t('adminDashboard.cms.upload_image')}
                      </button>
                      {(formData.image || (banner && preview !== resolveImageUrl(banner.image_url))) && (
                        <button 
                          type="button" 
                          className="ab-image-btn remove"
                          onClick={handleRemoveImage}
                        >
                          <X size={18} /> {t('adminDashboard.cms.remove_image')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="ab-upload-placeholder-large">
                  <UploadCloud size={48} />
                  <p>{t('adminDashboard.cms.click_to_upload')}</p>
                  <span>{t('adminDashboard.cms.max_size')}</span>
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                style={{ display: 'none' }}
              />
            </div>
          </div>

          <div className="ab-form-footer">
            <button type="button" className="ab-btn-cancel" onClick={onCancel} disabled={loading}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="ab-btn-submit" disabled={loading}>
              {loading ? t('common.saving') : banner ? t('adminDashboard.cms.edit_banner') : t('adminDashboard.cms.add_banner')}
            </button>
          </div>
        </form>
      </div>

      <BannerDetectionModal 
        isOpen={showConfirmModal}
        banner={foundBanner}
        onConfirm={handleConfirmSwitch}
        onCancel={handleCancelSwitch}
        t={t}
      />
    </div>
  );
};

// ── Confirmation Modal Sub-component ──
const BannerDetectionModal = ({ isOpen, banner, onConfirm, onCancel, t }) => {
  if (!isOpen || !banner) return null;

  return (
    <div className="ab-modal-overlay">
      <div className="ab-modal-card">
        <div className="ab-modal-icon-container">
          <Globe className="ab-modal-icon" size={32} />
        </div>
        <h3>{t('adminDashboard.cms.banner_exists_title', 'Banner Already Exists')}</h3>
        <p>
          {t('adminDashboard.cms.banner_exists_message', 'A banner already exists for this country. Would you like to switch to editing the existing one?')}
        </p>
        
        <div className="ab-modal-preview">
          <div className="ab-modal-preview-info">
            <strong>{banner.title}</strong>
            <span>{banner.country_code}</span>
          </div>
          <div className="ab-modal-preview-img">
            <img src={resolveImageUrl(banner.image_url)} alt={banner.title} />
          </div>
        </div>

        <div className="ab-modal-actions">
          <button className="ab-modal-btn-no" onClick={onCancel}>
            {t('common.no', 'No')}
          </button>
          <button className="ab-modal-btn-yes" onClick={onConfirm}>
            {t('common.yes', 'Yes, Continue')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppBannerForm;
